import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { formatDuration } from '@/lib/format-duration';

export interface ProgressBarProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Progress value (0-100) */
  value?: number;
  /** Indeterminate mode - shows loading without known progress */
  indeterminate?: boolean;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Color variant */
  variant?: 'primary' | 'success' | 'warning' | 'danger';
  /** Label above the progress bar */
  label?: string;
  /** Show percentage text */
  showPercentage?: boolean;
  /** Estimated time remaining in seconds */
  estimatedTimeRemaining?: number;
  /** Animate the progress fill */
  animated?: boolean;
}

const sizeClasses = {
  sm: 'h-1',
  md: 'h-2',
  lg: 'h-3',
};

const variantClasses = {
  primary: 'bg-primary',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
};

const variantGlowClasses = {
  primary: 'shadow-glow-subtle',
  success: 'shadow-glow-success',
  warning: 'shadow-[0_0_10px_rgba(212,165,116,0.3)]',
  danger: 'shadow-glow-error',
};

export function ProgressBar({
  value = 0,
  indeterminate = false,
  size = 'md',
  variant = 'primary',
  label,
  showPercentage = false,
  estimatedTimeRemaining,
  animated = true,
  className,
  ...props
}: ProgressBarProps) {
  const { t } = useTranslation('common');
  const clampedValue = Math.max(0, Math.min(100, value));

  return (
    <div className={cn('w-full', className)} {...props}>
      {/* Header with label and metadata */}
      {(label || showPercentage || estimatedTimeRemaining !== undefined) && (
        <div className="flex items-center justify-between mb-1">
          {label && (
            <span className="text-sm font-medium text-fg-secondary">{label}</span>
          )}
          <div className="flex items-center gap-3 text-xs text-fg-muted">
            {showPercentage && !indeterminate && <span>{Math.round(clampedValue)}%</span>}
            {estimatedTimeRemaining !== undefined && !indeterminate && (
              <span>
                ~{formatDuration(estimatedTimeRemaining)}{' '}
                {t('common.timeRemaining', 'remaining')}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Progress bar container */}
      <div
        className={cn(
          'w-full overflow-hidden rounded-full bg-bg-surface',
          sizeClasses[size]
        )}
        role="progressbar"
        aria-valuenow={indeterminate ? undefined : clampedValue}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-busy={indeterminate}
      >
        {/* Progress fill */}
        {indeterminate ? (
          <div
            className={cn(
              'h-full w-1/3 animate-shimmer',
              variantClasses[variant],
              variantGlowClasses[variant],
              'bg-linear-to-r from-transparent via-current to-transparent',
              'motion-reduce:animate-none'
            )}
            style={{
              backgroundSize: '200% 100%',
            }}
          />
        ) : (
          <div
            className={cn(
              'h-full',
              variantClasses[variant],
              variantGlowClasses[variant],
              // Gradient for depth
              'bg-linear-to-r from-current to-transparent',
              'relative overflow-hidden',
              // Smooth transition for value changes
              animated && 'transition-all duration-150 ease-standard',
              'motion-reduce:transition-none'
            )}
            style={{
              width: `${clampedValue}%`,
              backgroundImage:
                variant === 'primary'
                  ? 'linear-gradient(90deg, #4db8b3, #66c9c5)'
                  : undefined,
            }}
          >
            {/* Subtle shimmer effect on the fill */}
            {animated && (
              <div className="absolute inset-0 animate-shimmer bg-linear-to-r from-transparent via-white/10 to-transparent" />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Compound component for multi-step progress
export interface MultiStepProgressProps {
  steps: {
    label: string;
    completed: boolean;
    current?: boolean;
  }[];
  className?: string;
}

export function MultiStepProgress({ steps, className }: MultiStepProgressProps) {
  const completedCount = steps.filter((s) => s.completed).length;
  const progress = (completedCount / steps.length) * 100;

  return (
    <div className={cn('space-y-2', className)}>
      <ProgressBar
        value={progress}
        size="sm"
        variant="primary"
        showPercentage
        label={`Step ${completedCount} of ${steps.length}`}
      />
      <div className="flex justify-between text-xs text-fg-muted">
        {steps.map((step) => (
          <span
            key={step.label}
            className={cn(
              'transition-all duration-150',
              step.completed && 'text-primary',
              step.current && 'text-fg font-medium'
            )}
          >
            {step.label}
          </span>
        ))}
      </div>
    </div>
  );
}

// Compound component for file upload progress
export interface UploadProgressProps {
  fileName: string;
  progress: number;
  size?: string;
  speed?: string;
  className?: string;
}

export function UploadProgress({
  fileName,
  progress,
  size,
  speed,
  className,
}: UploadProgressProps) {
  return (
    <div className={cn('space-y-1 p-3 rounded-lg bg-bg-elevated', className)}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-fg truncate mr-2">{fileName}</span>
        {size && <span className="text-xs text-fg-muted">{size}</span>}
      </div>
      <ProgressBar value={progress} size="sm" variant="primary" showPercentage />
      {speed && <span className="text-xs text-fg-subtle">{speed}</span>}
    </div>
  );
}
